import { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';

export interface LegalDocumentModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback when the modal is closed (X button, backdrop, Escape key)
   * Note: Closing does NOT mark the document as reviewed
   */
  onClose: () => void;
  /**
   * Callback when user has scrolled to bottom and clicks "I've Read This"
   * Only this action marks the document as reviewed
   */
  onAcknowledge: () => void;
  /**
   * Modal title displayed in header
   */
  title: string;
  /**
   * The legal content to display
   */
  children: React.ReactNode;
  /**
   * Document type for tracking purposes
   */
  documentType: 'privacy' | 'terms';
}

/**
 * LegalDocumentModal Component
 *
 * A modal for displaying legal documents (Privacy Policy, Terms of Service)
 * with scroll-to-bottom requirement before acknowledgment.
 *
 * Key behaviors:
 * - User must scroll to the bottom of the document
 * - "I've Read This" button is disabled until scrolled to bottom
 * - Closing the modal early (X, backdrop, Escape) does NOT mark as reviewed
 * - Progress bar shows scroll progress
 */
export default function LegalDocumentModal({
  isOpen,
  onClose,
  onAcknowledge,
  title,
  children,
  documentType,
}: LegalDocumentModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScrollProgress(0);
      setHasScrolledToBottom(false);
      // Scroll to top when opening
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }
  }, [isOpen]);

  // Check if content is short enough to not require scrolling
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      const { scrollHeight, clientHeight } = scrollRef.current;
      // If content fits without scrolling, enable button immediately
      if (scrollHeight <= clientHeight + 20) {
        setHasScrolledToBottom(true);
        setScrollProgress(100);
      }
    }
  }, [isOpen, children]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;

    // Prevent division by zero
    if (maxScroll <= 0) {
      setScrollProgress(100);
      setHasScrolledToBottom(true);
      return;
    }

    const progress = Math.min(100, (scrollTop / maxScroll) * 100);
    setScrollProgress(progress);

    // Consider "bottom" reached when within 20px of actual bottom
    const threshold = 20;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${documentType}-modal-title`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2
            id={`${documentType}-modal-title`}
            className="text-xl font-semibold text-gray-900"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="p-6 overflow-y-auto flex-1"
        >
          {children}
        </div>

        {/* Scroll Progress Bar */}
        <div className="h-1 bg-gray-200 flex-shrink-0">
          <div
            className="h-full bg-primary-600 transition-all duration-150"
            style={{ width: `${scrollProgress}%` }}
            role="progressbar"
            aria-valuenow={Math.round(scrollProgress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Reading progress"
          />
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <p
              className={cn(
                'text-sm',
                hasScrolledToBottom ? 'text-green-600' : 'text-gray-500'
              )}
            >
              {hasScrolledToBottom ? (
                <span className="flex items-center gap-1.5">
                  <CheckIcon className="w-4 h-4" />
                  You've reviewed this document
                </span>
              ) : (
                'Please scroll to the bottom to continue'
              )}
            </p>
            <Button
              type="button"
              onClick={onAcknowledge}
              disabled={!hasScrolledToBottom}
            >
              I've Read This
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}
